import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { Search, Download, CheckSquare, Square, X, Loader2, Trash2, RefreshCw } from "lucide-react";
import { useBoletos } from "../../hooks/useBoletos";

export default function DescargarBoletosModal({ isOpen, onClose, eventoId }) {
  const { listarBoletosDisponibles, descargarBoletosMasivo, eliminarBoletos } = useBoletos();

  const [boletos, setBoletos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [error, setError] = useState(null);

  // Cargar lista al abrir
  useEffect(() => {
    if (!isOpen || !eventoId) return;
    setBusqueda("");
    setSeleccionados(new Set());
    setError(null);

    const cargar = async () => {
      setCargando(true);
      try {
        const result = await listarBoletosDisponibles(eventoId);
        if (result.success) {
          setBoletos(result.data.boletos || []);
        } else {
          setError(result.error || "Error al cargar boletos");
        }
      } finally {
        setCargando(false);
      }
    };
    cargar();
  }, [isOpen, eventoId, listarBoletosDisponibles]);

  // Filtrar por búsqueda
  const boletosFiltrados = boletos.filter((b) =>
    b.nombre.toLowerCase().includes(busqueda.toLowerCase())
  );

  const todosSeleccionados =
    boletosFiltrados.length > 0 &&
    boletosFiltrados.every((b) => seleccionados.has(b.invitadoId));

  const algunoSeleccionado = boletosFiltrados.some((b) =>
    seleccionados.has(b.invitadoId)
  );

  const toggleSeleccion = useCallback((invitadoId) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(invitadoId)) next.delete(invitadoId);
      else next.add(invitadoId);
      return next;
    });
  }, []);

  const toggleTodos = useCallback(() => {
    if (todosSeleccionados) {
      setSeleccionados((prev) => {
        const next = new Set(prev);
        boletosFiltrados.forEach((b) => next.delete(b.invitadoId));
        return next;
      });
    } else {
      setSeleccionados((prev) => {
        const next = new Set(prev);
        boletosFiltrados.forEach((b) => next.add(b.invitadoId));
        return next;
      });
    }
  }, [todosSeleccionados, boletosFiltrados]);

  const handleDescargar = async () => {
    if (seleccionados.size === 0 || descargando) return;
    setDescargando(true);
    setError(null);
    try {
      const result = await descargarBoletosMasivo(eventoId, [...seleccionados]);
      if (!result.success) {
        setError(result.error || "Error al descargar");
      } else {
        onClose();
      }
    } finally {
      setDescargando(false);
    }
  };

  const handleBorrarBoletos = async () => {
    if (!window.confirm("¿Eliminar todos los boletos generados? Se regenerarán al descargar.")) return;
    setBorrando(true);
    setError(null);
    try {
      const result = await eliminarBoletos(eventoId);
      if (result.success) {
        // Recargar lista para reflejar que ya no están generados
        const r = await listarBoletosDisponibles(eventoId);
        if (r.success) setBoletos(r.data.boletos || []);
      } else {
        setError(result.error || "Error al eliminar");
      }
    } finally {
      setBorrando(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-lg bg-white dark:bg-[#1e1e1e] rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
            <DialogTitle className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              Descargar boletos
            </DialogTitle>
            <div className="flex items-center gap-2">
              <button
                onClick={handleBorrarBoletos}
                disabled={borrando}
                title="Borrar boletos generados para regenerarlos"
                className="flex items-center gap-1 px-3 py-1.5 text-xs text-red-500 hover:text-red-700 border border-red-200 hover:border-red-400 rounded-lg transition disabled:opacity-50"
              >
                {borrando ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                Borrar generados
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Buscador */}
          <div className="px-6 pt-4 pb-2">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Buscar por nombre..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-casal/40"
              />
            </div>
          </div>

          {/* Seleccionar todos */}
          {!cargando && boletosFiltrados.length > 0 && (
            <div className="px-6 py-2 flex items-center justify-between text-sm">
              <button
                onClick={toggleTodos}
                className="flex items-center gap-2 text-casal hover:text-casal/80 font-medium transition"
              >
                {todosSeleccionados ? (
                  <CheckSquare size={16} />
                ) : (
                  <Square size={16} />
                )}
                {todosSeleccionados ? "Desmarcar todos" : "Seleccionar todos"}
                {busqueda && ` (${boletosFiltrados.length} visibles)`}
              </button>
              <span className="text-gray-400">
                {seleccionados.size} seleccionado{seleccionados.size !== 1 ? "s" : ""}
              </span>
            </div>
          )}

          {/* Lista */}
          <div className="flex-1 overflow-y-auto px-6 py-2 min-h-0">
            {cargando ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 size={24} className="animate-spin text-casal" />
                <span className="ml-3 text-sm text-gray-500">Cargando boletos...</span>
              </div>
            ) : error ? (
              <div className="text-center py-10 text-red-500 text-sm">{error}</div>
            ) : boletosFiltrados.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">
                {busqueda
                  ? "No hay resultados para esa búsqueda"
                  : "No hay boletos generados para este evento"}
              </div>
            ) : (
              <ul className="space-y-1">
                {boletosFiltrados.map((boleto) => {
                  const marcado = seleccionados.has(boleto.invitadoId);
                  return (
                    <li key={boleto.invitadoId}>
                      <button
                        onClick={() => toggleSeleccion(boleto.invitadoId)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition ${
                          marcado
                            ? "bg-casal/10 text-casal"
                            : "hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                        }`}
                      >
                        {marcado ? (
                          <CheckSquare size={16} className="shrink-0" />
                        ) : (
                          <Square size={16} className="shrink-0 text-gray-300" />
                        )}
                        <span className="text-sm font-medium truncate flex-1">
                          {boleto.nombre}
                        </span>
                        {boleto.generado ? (
                          <span className="text-xs text-green-500 shrink-0">✓</span>
                        ) : (
                          <span className="text-xs text-gray-300 shrink-0"><RefreshCw size={11} /></span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition"
            >
              Cancelar
            </button>
            <button
              onClick={handleDescargar}
              disabled={seleccionados.size === 0 || descargando}
              className="flex items-center gap-2 px-5 py-2 bg-casal text-white text-sm font-medium rounded-lg hover:bg-casal/90 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {descargando ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Generando ZIP...
                </>
              ) : (
                <>
                  <Download size={16} />
                  Descargar {seleccionados.size > 0 ? `(${seleccionados.size})` : ""}
                </>
              )}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
